/**
 * Production Prep API
 *
 * API functions for the production prep workflow.
 */

import { api } from './client';
import type { TrackedOrder } from '../types';

// Response types
interface PrepOrderFile {
  id: string;
  type: string;
  originalFilename: string;
  mimeType: string;
  downloadUrl: string | null;
  selectedForProduction: boolean;
  embroideryMetadata?: {
    totalStitches: number;
    colorChanges: number;
    widthMm: number;
    heightMm: number;
    widthIn: number;
    heightIn: number;
    format: string;
  } | null;
}

export interface ParsedHat {
  shopifyLineItemId: string;
  productTitle: string;
  variantTitle: string;
  color: string;
  size: string;
  quantity: number;
}

export interface HatAssignmentDraft {
  sourceLineItemId?: string;
  color: string;
  size: string;
  quantity: number;
  isManualOverride?: boolean;
}

export interface AssignmentDraft {
  id?: string;
  mockupFileId: string;
  digitizationFileId?: string;
  label?: string;
  productType?: 'embroidery' | 'patch';
  hatItems: HatAssignmentDraft[];
  status?: 'pending' | 'completed';
  productionData?: unknown;
}

export interface PrepOrder {
  id: string;
  shopifyOrderName: string;
  shopifyOrderNumber: string;
  customerName: string;
  customerEmail: string;
  productType: 'embroidery' | 'patch' | 'both';
  lineItems: TrackedOrder['lineItems'];
  shopifyNote?: string;
  currentStage: string;
  createdAt: string | null;
  updatedAt: string | null;
  files: PrepOrderFile[];
  parsedHats: ParsedHat[];
  productionAssignments: AssignmentDraft[];
  logoMappings?: Record<string, string>;
}

interface GetOrdersResponse {
  orders: PrepOrder[];
}

interface SaveAssignmentsResponse {
  success: boolean;
  assignments: AssignmentDraft[];
}

interface SelectFileResponse {
  success: boolean;
  fileId: string;
  selectedForProduction: boolean;
}

interface SendToProductionResponse {
  success: boolean;
  order: {
    id: string;
    currentStage: string;
  };
}

/**
 * Get all orders in APPROVED stage with files
 */
export async function getProductionPrepOrders(): Promise<GetOrdersResponse> {
  return api.get<GetOrdersResponse>('/production-prep/orders');
}

/**
 * Toggle file selection for production
 */
export async function selectFileForProduction(
  fileId: string,
  selected: boolean
): Promise<SelectFileResponse> {
  return api.put<SelectFileResponse>(`/production-prep/files/${fileId}/select`, {
    selected,
  });
}

/**
 * Save production assignments for an order
 */
export async function saveAssignments(
  orderId: string,
  assignments: AssignmentDraft[]
): Promise<SaveAssignmentsResponse> {
  return api.put<SaveAssignmentsResponse>(
    `/production-prep/orders/${orderId}/assignments`,
    { assignments }
  );
}

/**
 * Send order to production (move from APPROVED to IN_PRODUCTION)
 */
export async function sendOrderToProduction(
  orderId: string,
  allowPartial?: boolean
): Promise<SendToProductionResponse> {
  return api.post<SendToProductionResponse>(
    `/production-prep/orders/${orderId}/send-to-production`,
    allowPartial ? { allowPartial: true } : undefined
  );
}

/**
 * Build download URL for a file with custom naming
 */
export function buildDownloadUrl(
  fileId: string,
  customerName: string,
  orderNumber: string
): string {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
  const params = new URLSearchParams({
    customerName: customerName.toLowerCase(),
    orderNumber,
  });
  return `${baseUrl}/production-prep/files/${fileId}/download?${params.toString()}`;
}
